const base =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(
    /\/$/,
    "",
  ) ?? "http://127.0.0.1:8080";

export type Bootstrap = {
  tenantId: string;
  organisationName: string;
  role: "owner" | "admin" | "marketing" | "sales" | "analyst" | "viewer";
  onboardingCompleted: boolean;
  selectedPlan: {
    slug: string;
    name: string;
    status: string;
    trialEndsAt: string | null;
  };
};

export type PublicPlan = {
  id: string;
  versionId: string;
  slug: "launch" | "growth" | "scale" | "enterprise";
  name: string;
  description: string;
  version: number;
  trialDays: number;
  entitlements: Record<string, unknown>;
  keyFeatures: string[];
  prices: Record<"monthly" | "annual", {
    interval: "monthly" | "annual";
    currency: string;
    amountMinor: number | null;
    configured: boolean;
  }>;
};

export type CommercialSummary = {
  configured: boolean;
  billingConfigured: boolean;
  plan: PublicPlan;
  subscription: {
    status: string;
    interval: "monthly" | "annual";
    intendedPlanSlug: string;
    conversionMode: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  };
  trial: {
    status: string;
    startsAt: string;
    endsAt: string;
    daysRemaining: number;
    retentionUntil: string;
    promotionalCredits: number;
    noCardRequired: true;
    automaticCharge: false;
  } | null;
  paymentMethod: {
    present: boolean;
    brand: string | null;
    last4: string | null;
    expiryMonth: number | null;
    expiryYear: number | null;
  };
  usage: {
    fullUsers: number;
    reviewers: number;
    agents: number;
    leads: number;
    integrations: number;
    campaigns: number;
    nurtureJourneys: number;
    credits: {
      subscription: number;
      promotional: number;
      purchased: number;
      adjustment: number;
      consumed: number;
      remaining: number;
      nextExpiration: { amount: number; expiresAt: string } | null;
    };
    byAgent: Array<{ name: string; credits: number; operations: number }>;
    byUser: Array<{ name: string; credits: number; operations: number }>;
    byWorkflow: Array<{ name: string; credits: number; operations: number }>;
  };
  activatedAgents: string[];
  creditPacks: Array<{ slug: string; credits: number; priceMinor: number; currency: string; configured: boolean }>;
  autoRecharge: {
    enabled: boolean;
    creditPack: string | null;
    thresholdCredits: number;
    maximumMonthlyPurchases: number;
    purchasesThisMonth: number;
  };
};

export type ScannedPage = {
  url: string;
  title: string;
  description: string;
  primaryHeading: string;
  wordCount: number;
  internalLinks: number;
  forms: number;
  callsToAction: number;
  score: number;
  opportunities: string[];
  content: string;
  aiCategory: string;
  aiReport: string;
  ctaLabel: string;
  isHttps: boolean;
  hasViewportMeta: boolean;
  hasStructuredData: boolean;
  canonicalUrl: string;
  metaRobots: string;
  htmlLang: string;
  headingCounts: { h1: number; h2: number; h3: number };
  imagesTotal: number;
  imagesWithAlt: number;
  responseTimeMs: number | null;
};

// The same High/Medium/Low-style severity model used elsewhere in the
// product -- see services/website-intelligence-service for the analyzers
// that produce these.
export type WebsiteSeverity = "critical" | "high" | "medium" | "low";
export type WebsiteAnalysisCategory = "content" | "seo" | "ux" | "conversion" | "technical" | "accessibility";

export type WebsiteFinding = {
  id: string;
  category: WebsiteAnalysisCategory;
  severity: WebsiteSeverity;
  title: string;
  description: string;
  pageUrl: string;
};

export type WebsiteRecommendation = {
  id: string;
  category: WebsiteAnalysisCategory;
  priority: WebsiteSeverity;
  what: string;
  why: string;
  impact: string;
  suggestedAction: string;
  relatedFindingIds: string[];
};

export type WebsiteSubScores = Record<WebsiteAnalysisCategory, number>;

export type WebsiteScan = ScannedPage & {
  scannedAt: string;
  pagesScanned: number;
  pagesDiscovered: number;
  discoveryMethod: "sitemap" | "homepage-links" | "homepage-only";
  pages: ScannedPage[];
  pagesFailed: Array<{ url: string; reason: string }>;
  brandPositioning: string;
  websiteTone: string;
  brandVoice: string;
  aiRecommendation: string;
  pageFilter: string;
  aiExecutiveSummary: string;
  websiteGoal: string;
  productsServices: string[];
  targetAudience: string;
  scanHistory: Array<{ scannedAt: string; score: number; pagesScanned: number }>;
  // Null until a scan has run the scoring stage -- never fabricated.
  subScores: WebsiteSubScores | null;
  findings: WebsiteFinding[];
  recommendations: WebsiteRecommendation[];
};

export type Workspace = Bootstrap & {
  company: Record<string, unknown>;
  commercial: Record<string, unknown>;
  revenueSources: Array<{ name: string; type: string; annualValue: number }>;
  integrations: Array<{ provider: string; status: string; lastSyncedAt: string | null }>;
  integrationRecordCounts: Array<{ entityType: string; count: number }>;
  websiteScan: WebsiteScan | null;
  websiteScanStatus: "idle" | "queued" | "running" | "completed" | "failed";
  websiteScanError: string | null;
  agentRuns: Array<{
    agent: string;
    version: string;
    taskType: string;
    status: string;
    summary: string;
    blockers: string[];
    confidence: number | null;
    output: Record<string, unknown>;
    startedAt: string;
  }>;
  agents: Array<{
    id: string;
    name: string;
    description: string;
    category: string;
    version: string;
    capabilities: string[];
    dependencies: string[];
    status: string;
    summary: string;
    blockers: string[];
    confidence: number | null;
    output: Record<string, unknown>;
    startedAt: string | null;
    eligible: boolean;
    active: boolean;
  }>;
  billing: CommercialSummary;
};

export type OnboardingSubmission = {
  company: {
    name: string;
    websiteUrl: string;
    industry: string;
    countryCode: string;
    employeeBand: string;
    businessModel: string;
    valueProposition: string;
    targetMarkets: string[];
    primaryGoal: string;
  };
  commercial: {
    annualRevenueTarget: number;
    currentRevenue: number;
    currentPipeline: number;
    averageDealValue: number;
    leadToOpportunityRate: number;
    opportunityWinRate: number;
    salesCycleDays: number;
    currency: string;
  };
  revenueSources: Array<{
    name: string;
    type:
      | "product"
      | "service"
      | "subscription"
      | "commerce"
      | "partner"
      | "other";
    annualValue: number;
  }>;
  integrations: Array<
    | "hubspot"
    | "salesforce"
    | "google-analytics"
    | "linkedin"
    | "mailchimp"
    | "stripe"
    | "slack"
  >;
  scanWebsite: boolean;
  externalAnalysisConsent: true;
};

export type CompanyProfileUpdate = Pick<
  OnboardingSubmission,
  "company" | "commercial" | "revenueSources"
>;

// The in-memory session token is only ever populated once, at sign-in or
// app-load time (see App()'s initialise()) -- there's no periodic refresh,
// so a long-lived tab can end up with a token the API now rejects as
// expired even though the user never signed out. Rather than surfacing that
// as a dead-end "sign in is required" error (previously the only way out
// was a full page reload, which re-runs getSession() from scratch), App()
// registers a refresh handler here that re-reads the session and updates
// React state; a single 401 transparently retries once with the refreshed
// token before giving up.
let refreshSession: (() => Promise<string | null>) | null = null;
export function registerSessionRefresh(
  handler: () => Promise<string | null>,
) {
  refreshSession = handler;
}

async function request<T>(
  path: string,
  token: string | null,
  tenantId?: string,
  init: RequestInit = {},
  isRetry = false,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);
  headers.set("accept", "application/json");
  if (tenantId) headers.set("x-tenant-id", tenantId);
  if (init.body) headers.set("content-type", "application/json");
  let response: Response;
  try {
    response = await fetch(`${base}${path}`, { ...init, headers });
  } catch (cause) {
    throw new Error(
      `Clintra API could not be reached at ${base}. Check your connection and try again.`,
      { cause },
    );
  }
  if (response.status === 401 && !isRetry && refreshSession) {
    const freshToken = await refreshSession().catch(() => null);
    if (freshToken) return request<T>(path, freshToken, tenantId, init, true);
  }
  const body = (await response.json().catch(() => null)) as {
    error?: { message?: string; details?: unknown };
  } | null;
  if (!response.ok)
    throw new Error(
      body?.error?.message ?? `Request failed with ${response.status}`,
    );
  return body as T;
}

export const bootstrapWorkspace = (
  token: string,
  selectionToken?: string,
  inviteToken?: string,
) =>
  request<Bootstrap>("/api/v1/bootstrap", token, undefined, {
    headers: {
      ...(selectionToken ? { "x-plan-selection-token": selectionToken } : {}),
      ...(inviteToken ? { "x-invite-token": inviteToken } : {}),
    },
  });
export const loadWorkspace = (token: string, tenantId: string) =>
  request<Workspace>("/api/v1/workspace", token, tenantId);
export const updateCompanyProfile = (
  token: string,
  tenantId: string,
  input: CompanyProfileUpdate,
) =>
  request<Workspace>("/api/v1/workspace/company-profile", token, tenantId, {
    method: "PUT",
    body: JSON.stringify(input),
  });
export const completeOnboarding = (
  token: string,
  tenantId: string,
  input: OnboardingSubmission,
) =>
  request<Workspace>("/api/v1/onboarding/complete", token, tenantId, {
    method: "POST",
    body: JSON.stringify(input),
  });
export const rescanWebsite = (
  token: string,
  tenantId: string,
  pageFilter?: string,
) =>
  request<Workspace>("/api/v1/website-scans", token, tenantId, {
    method: "POST",
    body: JSON.stringify({ pageFilter: pageFilter ?? "" }),
  });
export const activateAgentNetwork = (token: string, tenantId: string) =>
  request<Workspace>("/api/v1/agents/activate", token, tenantId, {
    method: "POST",
  });
export type ConnectorManifest = {
  id: string;
  name: string;
  category: string;
  auth: "oauth2" | "api_key" | "webhook";
  capabilities: Array<{
    id: string;
    label: string;
    mode: "read" | "write" | "read_write";
    implemented: boolean;
  }>;
};
export const loadIntegrationsCatalog = (token: string, tenantId: string) =>
  request<{ data: ConnectorManifest[] }>("/api/v1/integrations", token, tenantId);
export const startIntegrationConnect = (
  token: string,
  tenantId: string,
  provider: string,
  // orgUrl is Dynamics-only, shopDomain is Shopify-only -- every other
  // provider ignores both.
  extra?: { orgUrl?: string; shopDomain?: string },
) =>
  request<{ authorizeUrl: string }>(
    `/api/v1/integrations/${provider}/connect`,
    token,
    tenantId,
    { method: "POST", body: JSON.stringify(extra ?? {}) },
  );
export const connectIntegrationWithApiKey = (
  token: string,
  tenantId: string,
  provider: string,
  apiKey: string,
  // baseUrl/clientSecret are Marketo-only -- every other api_key provider
  // (currently just Stripe) ignores both.
  extra?: { baseUrl?: string; clientSecret?: string },
) =>
  request<{ ok: boolean }>(
    `/api/v1/integrations/${provider}/connect`,
    token,
    tenantId,
    { method: "POST", body: JSON.stringify({ token: apiKey, ...extra }) },
  );
export const disconnectIntegration = (
  token: string,
  tenantId: string,
  provider: string,
) =>
  request<{ ok: boolean }>(
    `/api/v1/integrations/${provider}/disconnect`,
    token,
    tenantId,
    { method: "POST" },
  );
export const syncIntegration = (token: string, tenantId: string, provider: string) =>
  request<{ ok: boolean; recordsWritten: number }>(
    `/api/v1/integrations/${provider}/sync`,
    token,
    tenantId,
    { method: "POST" },
  );
export const syncAllIntegrations = (token: string, tenantId: string) =>
  request<{ results: Array<{ provider: string; ok: boolean; message: string }> }>(
    "/api/v1/integrations/sync",
    token,
    tenantId,
    { method: "POST" },
  );

// ---- Team ----

export type MembershipRole =
  | "owner"
  | "admin"
  | "marketing"
  | "sales"
  | "analyst"
  | "viewer"
  | "reviewer";

export type TeamMember = {
  id: string;
  userId: string;
  email: string;
  name: string | null;
  emailVerified: boolean | null;
  role: MembershipRole;
  joinedAt: string;
};

export type InviteEmailStatus = "sent" | "failed" | "not_configured";

export type TeamInvite = {
  id: string;
  email: string;
  role: MembershipRole;
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
  link: string | null;
  emailStatus: InviteEmailStatus | null;
  emailError: string | null;
};

export type PublicInvitePreview =
  | { valid: false }
  | { valid: true; organisationName: string; role: MembershipRole; email: string };

export const loadTeamMembers = (token: string, tenantId: string) =>
  request<{ data: TeamMember[] }>("/api/v1/team/members", token, tenantId);
export const loadTeamInvites = (token: string, tenantId: string) =>
  request<{ data: TeamInvite[] }>("/api/v1/team/invites", token, tenantId);
export const createTeamInvite = (
  token: string,
  tenantId: string,
  email: string,
  role: MembershipRole,
) =>
  request<TeamInvite>("/api/v1/team/invites", token, tenantId, {
    method: "POST",
    body: JSON.stringify({ email, role }),
  });
export const revokeTeamInvite = (token: string, tenantId: string, inviteId: string) =>
  request<{ ok: boolean }>(`/api/v1/team/invites/${inviteId}/revoke`, token, tenantId, {
    method: "POST",
  });
export const resendTeamInviteEmail = (token: string, tenantId: string, inviteId: string) =>
  request<TeamInvite>(`/api/v1/team/invites/${inviteId}/resend`, token, tenantId, {
    method: "POST",
  });
export const updateTeamMemberRole = (
  token: string,
  tenantId: string,
  memberId: string,
  role: MembershipRole,
) =>
  request<TeamMember>(`/api/v1/team/members/${memberId}/role`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ role }),
  });
export const removeTeamMember = (token: string, tenantId: string, memberId: string) =>
  request<{ ok: boolean }>(`/api/v1/team/members/${memberId}/remove`, token, tenantId, {
    method: "POST",
  });
export const loadPublicInvitePreview = (token: string) =>
  request<PublicInvitePreview>(`/api/v1/public/invites/${token}`, null);

// ---- API keys (Settings -> API) ----

export type ApiKey = {
  id: string;
  name: string;
  keyPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export const loadApiKeys = (token: string, tenantId: string) =>
  request<{ data: ApiKey[] }>("/api/v1/settings/api-keys", token, tenantId);
export const createApiKey = (token: string, tenantId: string, name: string) =>
  request<{ key: ApiKey; secret: string }>("/api/v1/settings/api-keys", token, tenantId, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
export const revokeApiKey = (token: string, tenantId: string, keyId: string) =>
  request<{ ok: boolean }>(`/api/v1/settings/api-keys/${keyId}/revoke`, token, tenantId, {
    method: "POST",
  });

// ---- Lead Intelligence ----

// High/Medium/Low is the one priority model shown everywhere -- Overview
// counts, Priority Queue badges, the filter, and AI Recommendation text all
// derive from the same score thresholds (see DEFAULT_LEAD_SCORING_CONFIG.bands
// on the backend). Never introduce a different threshold or label elsewhere.
export type LeadBand = "high" | "medium" | "low";

export type LeadIntelligenceOverview = {
  crmConnected: boolean;
  icpConfigured: boolean;
  counts: { leads: number; scored: number; unscored: number; high: number; medium: number; low: number };
  averageScore: number | null;
  lastScoringRun: { startedAt: string; completedAt: string | null; leadsScored: number; scoresWritten: number; insufficientData: number } | null;
  dataSources: Array<{ provider: string; connected: boolean; lastSyncedAt: string | null; behavioralSupported: boolean; behavioralSynced: boolean }>;
};

// Provenance/context, never an ICP scoring input. "manual"/"csv" describe
// how the lead entered Clintra (Manual Entry form / CSV Import) -- Email and
// Marketing Campaign are not separate ingestion pipelines, they're labels a
// user assigns to a manual/CSV row (see lead.originalSource).
export type LeadSourceProvider = "hubspot" | "salesforce" | "zoho" | "pipedrive" | "dynamics" | "manual" | "csv";

export type LeadListItem = {
  id: string;
  name: string;
  email: string | null;
  title: string | null;
  company: string | null;
  industry: string | null;
  employeeBand: string | null;
  stage: string;
  score: number | null;
  band: LeadBand | null;
  scoredAt: string | null;
  lastActivityAt: string | null;
  source: { primary: LeadSourceProvider | null; additional: LeadSourceProvider[] };
};

export type LeadListQuery = {
  page?: number;
  pageSize?: number;
  sort?: "score" | "name" | "company" | "lastActivity" | "createdAt" | "stage";
  direction?: "asc" | "desc";
  band?: LeadBand;
  search?: string;
  source?: LeadSourceProvider;
};

export type LeadListResponse = {
  data: LeadListItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export type ScoreReason = { label: string; delta: number; detail?: string };
export type ScoreComponent = {
  id: string;
  label: string;
  available: boolean;
  value: number | null;
  weight: number;
  points: number | null;
  contributionCeiling: number | null;
  unavailableReason: string | null;
  reasons: ScoreReason[];
  subScores: Array<{ id: string; label: string; value: number | null; weight: number; evaluable: boolean; note: string }>;
};

export type ScoreChangeExplanation = {
  from: { scoreId: string; score: number; band: LeadBand; calculatedAt: string; modelVersion: string; trigger: string };
  to: { scoreId: string; score: number; band: LeadBand; calculatedAt: string; modelVersion: string; trigger: string };
  delta: number;
  bandChanged: boolean;
  configChanged: boolean;
  components: Array<{
    id: string; label: string;
    previous: { available: boolean; value: number | null; points: number | null };
    current: { available: boolean; value: number | null; points: number | null };
    pointsDelta: number; availabilityChanged: "gained_data" | "lost_data" | null;
    reasonsAdded: string[]; reasonsRemoved: string[]; summary: string;
  }>;
  renormalisationDelta: number;
  headline: string;
};

// Generated deterministically (evaluateLeadRecommendation), never an
// independent AI judgment. Approving/rejecting only records a decision --
// it never itself contacts the lead or changes CRM data.
export type LeadRecommendation = {
  id: string;
  title: string;
  rationale: string;
  actionType: string | null;
  status: "pending" | "approved" | "rejected" | "expired";
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
};

export type LeadDetail = {
  lead: {
    id: string; name: string; email: string | null; title: string | null; stage: string; consentStatus: string; originalSource: string | null;
    createdAt: string; lastActivityAt: string | null; company: string | null; domain: string | null; industry: string | null;
    employeeBand: string | null; revenueBand: string | null; countryCode: string | null; accountSource: string | null;
    score: number | null; band: LeadBand | null; disqualifiedReason: string | null;
  };
  // Provenance/context only -- never a scoring input, never auto-triggers
  // outreach/assignment/CRM changes. primary is null only if a lead somehow
  // has no ingestion link at all; the UI must show "Source not available"
  // rather than fabricating a source in that case.
  source: {
    primary: LeadSourceProvider | null;
    additional: LeadSourceProvider[];
    firstCapturedAt: string | null;
    lastSyncedAt: string | null;
    sourceStatus: string | null;
    originalSource: string | null;
  };
  score: {
    id: string | null;
    status: "scored" | "insufficient_data";
    score: number | null; band: LeadBand | null; confidence: number;
    components: Record<string, ScoreComponent>; explanation: string[]; calculatedAt: string | null;
  };
  scoreHistory: Array<{ id: string; score: number; band: LeadBand; calculatedAt: string }>;
  scoreChange: ScoreChangeExplanation | null;
  activityTimeline: Array<{ kind: string; source: string; strength: number; occurredAt: string; consented: boolean; metadata: Record<string, unknown> }>;
  recommendations: LeadRecommendation[];
  // Real optional LLM narration of the deterministic explanation above --
  // null whenever no OPENAI_API_KEY/entitled plan is configured, or the
  // score has too little explanation to narrate. Never a substitute for
  // the deterministic explanation, which always stands on its own.
  explanationNarrative: string | null;
};

export type IcpCompanyProfile = { industries: string[]; excludedIndustries: string[]; employeeBands: string[]; revenueBands: string[]; countries: string[]; businessModels: string[] };
export type IcpBuyerProfile = { titleKeywords: string[]; excludedTitleKeywords: string[]; seniorities: string[]; departments: string[] };
export type IcpQualificationSignals = {
  consentRequired: boolean; minActivityCount: number; minEngagementStrength: number; requiredActivityKinds: string[];
  excludeFreeEmailDomains: boolean; competitorDomains: string[]; activityWeights: Record<string, number>;
  lookbackDays: number; halfLifeDays: number; saturation: number;
};
export type IcpHistoricalSignals = { matchDimensions: string[]; lookbackDays: number; smoothing: number };

export type LeadScoringConfigResponse = {
  revision: number;
  icpConfigured: boolean;
  companyProfile: IcpCompanyProfile;
  buyerProfile: IcpBuyerProfile;
  qualificationSignals: IcpQualificationSignals;
  historicalSignals: IcpHistoricalSignals;
  weights: { icp_fit: number; firmographic: number; behavioral: number; historical: number; recent_activity: number };
  bands: { highMin: number; mediumMin: number };
  rescoreQueued?: boolean;
};

export type ModelLearningStats = {
  outcomes: { won: number; lost: number; open: number };
  readiness: { closedOpportunities: number; required: number; ready: boolean; message: string };
  insight: string | null;
};

const leadQuery = (query: LeadListQuery) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value !== undefined && value !== "") params.set(key, String(value));
  return params.size ? `?${params}` : "";
};

export const loadLeadIntelligenceOverview = (token: string, tenantId: string) =>
  request<LeadIntelligenceOverview>("/api/v1/lead-intelligence/overview", token, tenantId);

export const loadLeads = (token: string, tenantId: string, query: LeadListQuery = {}) =>
  request<LeadListResponse>(`/api/v1/lead-intelligence/leads${leadQuery(query)}`, token, tenantId);

export const loadLeadDetail = (token: string, tenantId: string, leadId: string) =>
  request<LeadDetail>(`/api/v1/lead-intelligence/leads/${encodeURIComponent(leadId)}`, token, tenantId);

export const loadLeadScoringConfig = (token: string, tenantId: string) =>
  request<LeadScoringConfigResponse>("/api/v1/lead-intelligence/config", token, tenantId);

export const saveLeadIcpConfig = (
  token: string,
  tenantId: string,
  input: { companyProfile: IcpCompanyProfile; buyerProfile: IcpBuyerProfile; qualificationSignals: IcpQualificationSignals; historicalSignals: IcpHistoricalSignals },
) =>
  request<LeadScoringConfigResponse>("/api/v1/lead-intelligence/config/icp", token, tenantId, { method: "PUT", body: JSON.stringify(input) });

export const saveLeadScoringWeights = (
  token: string,
  tenantId: string,
  input: { weights: { icpFit: number; firmographic: number; behavioral: number; historical: number; recentActivity: number } },
) =>
  request<LeadScoringConfigResponse>("/api/v1/lead-intelligence/config/weights", token, tenantId, { method: "PUT", body: JSON.stringify(input) });

export const refreshLeadScores = (token: string, tenantId: string) =>
  request<{ leadsProcessed: number; scoresWritten: number; insufficientData: number }>(
    "/api/v1/lead-intelligence/scores/refresh",
    token,
    tenantId,
    { method: "POST" },
  );

// Manual Entry and CSV Import share this row shape -- only email is
// required, everything else is optional context the backend enriches from
// where it can (e.g. inferring a company from the email domain).
export type ManualLeadRow = {
  firstName?: string;
  lastName?: string;
  email: string;
  company?: string;
  title?: string;
  industry?: string;
  employeeCount?: number;
  revenue?: number;
  country?: string;
  source?: string;
};

export type LeadMappingSummary = {
  leadsCreated: number;
  leadsUpdated: number;
  opportunitiesUpserted: number;
  activitiesUpserted: number;
  orphanActivities: number;
  skipped: Array<{ entityType: string; externalId: string; reason: string }>;
  skippedForCapacity: number;
  capacity: { used: number; limit: number | null };
  capabilities: Array<{ capability: string; ok: boolean; reason: string | null; message: string }>;
  touchedLeadIds: string[];
};

export const createManualLead = (token: string, tenantId: string, row: ManualLeadRow) =>
  request<LeadMappingSummary>("/api/v1/lead-intelligence/leads/manual", token, tenantId, { method: "POST", body: JSON.stringify(row) });

export const importLeadsCsv = (token: string, tenantId: string, rows: ManualLeadRow[], source?: string) =>
  request<LeadMappingSummary>("/api/v1/lead-intelligence/leads/import", token, tenantId, {
    method: "POST",
    body: JSON.stringify({ rows, ...(source ? { source } : {}) }),
  });

export const decideLeadRecommendation = (
  token: string,
  tenantId: string,
  leadId: string,
  recommendationId: string,
  decision: "approved" | "rejected",
) =>
  request<{ id: string; leadId: string | null; title: string; status: string; decidedBy: string | null; decidedAt: string | null }>(
    `/api/v1/lead-intelligence/leads/${encodeURIComponent(leadId)}/recommendations/${encodeURIComponent(recommendationId)}/decision`,
    token,
    tenantId,
    { method: "POST", body: JSON.stringify({ decision }) },
  );

export const loadModelLearning = (token: string, tenantId: string) =>
  request<ModelLearningStats>("/api/v1/lead-intelligence/model-learning", token, tenantId);

export const loadPublicPlans = (plan?: string, billing?: string) => {
  const query = new URLSearchParams();
  if (plan) query.set("plan", plan);
  if (billing) query.set("billing", billing);
  return request<{
    plans: PublicPlan[];
    selected: { plan: PublicPlan; price: PublicPlan["prices"]["monthly"] } | null;
    billing?: string;
  }>(`/api/v1/public/plans${query.size ? `?${query}` : ""}`, null);
};

export const createPlanSelection = (input: {
  plan: PublicPlan["slug"];
  billing: "monthly" | "annual";
  companyName: string;
  countryCode: string;
  companySize: string;
  primaryUseCase: string;
  attribution: Record<string, string>;
  acceptedTerms: true;
  acceptedPrivacy: true;
}) => request<{ selectionToken: string; expiresInSeconds: number }>(
  "/api/v1/public/plan-selections",
  null,
  undefined,
  { method: "POST", body: JSON.stringify(input) },
);

export const loadBilling = (token: string, tenantId: string) =>
  request<CommercialSummary>("/api/v1/billing", token, tenantId);

export const createSetupSession = (token: string, tenantId: string) =>
  request<{ id: string; url: string }>("/api/v1/billing/setup-session", token, tenantId, { method: "POST" });

export const createSubscriptionCheckout = (
  token: string,
  tenantId: string,
  input: { plan: PublicPlan["slug"]; billing: "monthly" | "annual"; conversion: "immediate" | "trial_end" },
) => request<{ id: string; url: string }>("/api/v1/billing/subscription-checkout", token, tenantId, { method: "POST", body: JSON.stringify(input) });

export const createCreditCheckout = (token: string, tenantId: string, creditPack: string) =>
  request<{ id: string; url: string }>("/api/v1/billing/credit-checkout", token, tenantId, { method: "POST", body: JSON.stringify({ creditPack }) });

export const createBillingPortal = (token: string, tenantId: string) =>
  request<{ id: string; url: string }>("/api/v1/billing/portal", token, tenantId, { method: "POST" });

export const setAgentActivation = (token: string, tenantId: string, agentId: string, action: "activate" | "deactivate") =>
  request<CommercialSummary>("/api/v1/agent-activations", token, tenantId, { method: "POST", body: JSON.stringify({ agentId, action }) });

export const loadPlatformReadiness = (token: string, tenantId: string) =>
  request<{ generatedAt: string; capabilities: Array<Record<string, unknown>> }>("/api/v1/admin/platform-readiness", token, tenantId);

export const loadInvoices = (token: string, tenantId: string) =>
  request<{ data: Array<{ number: string | null; currency: string; amountDueMinor: number; amountPaidMinor: number; status: string; hostedInvoiceUrl: string | null; invoicePdfUrl: string | null; createdAt: string }> }>("/api/v1/billing/invoices", token, tenantId);

export const loadCreditPurchases = (token: string, tenantId: string) =>
  request<{ data: Array<{ credits: number; amountMinor: number; currency: string; status: string; expiresAt: string | null; createdAt: string }> }>("/api/v1/billing/credit-purchases", token, tenantId);
export type UsageRecordEntry = {
  agentId: string;
  operation: string;
  model: string;
  creditsConsumed: number;
  status: string;
  userId: string;
  userName: string;
  createdAt: string;
};
export const loadUsageRecords = (token: string, tenantId: string) =>
  request<{ data: UsageRecordEntry[] }>("/api/v1/billing/usage-records", token, tenantId);

export type ApprovalItem = {
  id: string;
  entityType: string;
  agentId: string;
  title: string;
  summary: string | null;
  createdAt: string;
};
export const loadApprovals = (token: string, tenantId: string) =>
  request<{ data: ApprovalItem[] }>("/api/v1/approvals", token, tenantId);

export const updateAutoRecharge = (
  token: string,
  tenantId: string,
  input: { enabled: boolean; creditPack: string | null; thresholdCredits: number; maximumMonthlyPurchases: number },
) => request<typeof input & { updated: true }>("/api/v1/billing/auto-recharge", token, tenantId, { method: "PUT", body: JSON.stringify(input) });

export type Coupon = {
  id: string;
  code: string;
  credits: number;
  active: boolean;
  expiresAt: string | null;
  maxRedemptions: number | null;
  redemptionCount: number;
  createdAt: string;
};

export const loadCoupons = (token: string, tenantId: string) =>
  request<{ data: Coupon[] }>("/api/v1/billing/coupons", token, tenantId);

export const createCoupon = (
  token: string,
  tenantId: string,
  input: { code: string; credits: number; expiresAt?: string | null; maxRedemptions?: number | null },
) => request<{ id: string }>("/api/v1/billing/coupons", token, tenantId, { method: "POST", body: JSON.stringify(input) });

export const setCouponActive = (token: string, tenantId: string, couponId: string, active: boolean) =>
  request<{ updated: true }>(`/api/v1/billing/coupons/${couponId}`, token, tenantId, { method: "PUT", body: JSON.stringify({ active }) });

export const redeemCoupon = (token: string, tenantId: string, code: string) =>
  request<{ credits: number }>("/api/v1/billing/redeem-coupon", token, tenantId, { method: "POST", body: JSON.stringify({ code }) });

export async function downloadUsageCsv(token: string, tenantId: string) {
  const response = await fetch(`${base}/api/v1/usage/export.csv`, {
    headers: { authorization: `Bearer ${token}`, "x-tenant-id": tenantId },
  });
  if (!response.ok) throw new Error(`Usage export failed with ${response.status}`);
  return response.blob();
}

// ---- Revenue Orchestration ----
// The coordination layer over Clintra's specialist agents. Only Lead
// Intelligence and Website Intelligence have real, recurring pipelines --
// every number/status here is either real, an explicitly-labeled estimate,
// or an honest "not available" state. Nothing is fabricated.

export type RevenueHealth = "on_track" | "watch" | "at_risk" | "critical" | "insufficient_data";
export type OrchestrationSeverity = "critical" | "high" | "medium" | "low";
export type AgentActivityStatus = "not_run" | "running" | "complete" | "output_available" | "waiting" | "needs_review" | "failed";

export type RevenueSnapshot = {
  currency: string;
  target: number | null;
  targetAsOf: string | null;
  currentRevenue: number;
  pipeline: number;
  forecast: number | null;
  forecastUnavailableReason: string | null;
  historicalWinRate: number | null;
  gap: number | null;
  attainmentPct: number | null;
  coveragePct: number | null;
  health: RevenueHealth;
  healthReasons: string[];
  closedOpportunityCount: number;
  minClosedForForecast: number;
};

export type GapDriver = {
  key: string;
  title: string;
  description: string;
  estimatedImpact: number | null;
  isEstimate: boolean;
  severity: OrchestrationSeverity;
  responsibleAgent: string;
  evidence: string[];
  confidence: number;
};

export type DriftAlert = {
  key: string;
  title: string;
  severity: OrchestrationSeverity;
  description: string;
  affectedArea: string;
  recommendedResponse: string;
  sourceAgents: string[];
};

export type CrossAgentInsight = {
  key: string;
  summary: string;
  contributingAgents: string[];
  evidence: string[];
  confidence: number;
  revenueRelevance: string;
};

export type AgentActivityRow = {
  id: string;
  name: string;
  category: string;
  status: AgentActivityStatus;
  note: string;
  lastRunAt: string | null;
  isLiveTriggerable: boolean;
};

export type OrchestrationRecommendation = {
  id: string;
  title: string;
  problem: string;
  actionType: string | null;
  impact: string | null;
  recommendation: string | null;
  estimatedImpact: number | null;
  isEstimate: boolean;
  confidence: number | null;
  sourceAgents: string[];
  evidence: string[];
  priority: OrchestrationSeverity | null;
  status: "pending" | "approved" | "rejected" | "expired";
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
};

export type ActivityLogEntry = { timestamp: string; agent: string; message: string };

export type RevenueOrchestrationOverview = {
  snapshot: RevenueSnapshot;
  gapDrivers: GapDriver[];
  insights: CrossAgentInsight[];
  driftAlerts: DriftAlert[];
  agentActivity: AgentActivityRow[];
  recommendations: OrchestrationRecommendation[];
  activityLog: ActivityLogEntry[];
  hasPreviousCycle: boolean;
};

export type ScenarioLever = { type: "pipeline" | "winRate"; percentChange: number };
export type ScenarioResult = {
  lever: ScenarioLever;
  baselineForecast: number | null;
  scenarioForecast: number | null;
  delta: number | null;
  assumptions: string[];
};

export const loadRevenueOrchestrationOverview = (token: string, tenantId: string) =>
  request<RevenueOrchestrationOverview>("/api/v1/revenue-orchestration/overview", token, tenantId);

export const runRevenueOrchestrationCycle = (token: string, tenantId: string) =>
  request<{ recommendationsGenerated: number; snapshotRecorded: boolean }>("/api/v1/revenue-orchestration/cycle", token, tenantId, { method: "POST" });

export const decideRevenueRecommendation = (
  token: string,
  tenantId: string,
  recommendationId: string,
  decision: "approved" | "rejected",
  reason?: string,
) =>
  request<OrchestrationRecommendation>(
    `/api/v1/revenue-orchestration/recommendations/${encodeURIComponent(recommendationId)}/decision`,
    token,
    tenantId,
    { method: "POST", body: JSON.stringify({ decision, ...(reason ? { reason } : {}) }) },
  );

export const runRevenueScenario = (token: string, tenantId: string, lever: ScenarioLever) =>
  request<ScenarioResult>("/api/v1/revenue-orchestration/scenario", token, tenantId, { method: "POST", body: JSON.stringify(lever) });

// ---- Business Discovery ----
// The first agent a tenant meets -- establishes what the company sells, who
// it sells to, its market/category, and a draft ICP across six sections.
// Every fact is observed (directly supported by source material -- what the
// team typed at onboarding, or something the website content states),
// inferred (a reasonable conclusion from combining signals, above a
// minimum-evidence bar), or unknown -- never guessed.

export type FactStatus = "observed" | "inferred" | "unknown";
export type ConfidenceLevel = "high" | "medium" | "low";
export type ReviewStatus = "ai_identified" | "needs_confirmation" | "confirmed";
export type FactValue = string | string[] | number;

export type BusinessFact = {
  field: string;
  label: string;
  value: FactValue | null;
  status: FactStatus;
  source: string;
  evidence: string | null;
  confidenceScore: number | null;
  confidence?: ConfidenceLevel;
  reviewStatus?: ReviewStatus;
  note?: string;
  sampleSize?: number;
};

export type BusinessProfileSections = {
  company: BusinessFact[];
  offering: BusinessFact[];
  market: BusinessFact[];
  icp: BusinessFact[];
  positioning: BusinessFact[];
  competitive: BusinessFact[];
};

export type GapSeverity = "high" | "medium" | "low";
export type Gap = { key: string; question: string; relatedField: string; severity: GapSeverity };

export type SourceAnalysis = {
  onboardingDataPresent: boolean;
  websiteScanCompleted: boolean;
  websiteScanAt: string | null;
  observedAccountCount: number;
  observedAccountsSufficient: boolean;
  llmExtractionAvailable: boolean;
};

export type BusinessProfile = {
  sections: BusinessProfileSections;
  gaps: Gap[];
  sources: SourceAnalysis;
  overallConfidence: number | null;
  completeness: number;
};

export type BusinessProfileRecord = {
  id: string;
  revision: number;
  status: "draft" | "approved";
  profile: BusinessProfile;
  overallConfidence: number | null;
  completeness: number;
  generatedBy: "ai" | "human_edited";
  updatedBy: string;
  changeNote: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export const loadBusinessDiscoveryProfile = (token: string, tenantId: string) =>
  request<{ profile: BusinessProfileRecord | null }>("/api/v1/business-discovery/profile", token, tenantId);

export const runBusinessDiscovery = (token: string, tenantId: string) =>
  request<BusinessProfileRecord>("/api/v1/business-discovery/run", token, tenantId, { method: "POST" });

export const saveBusinessProfile = (token: string, tenantId: string, edits: Record<string, FactValue>, rejectedFields: string[] = [], changeNote?: string) =>
  request<BusinessProfileRecord>("/api/v1/business-discovery/profile", token, tenantId, {
    method: "PUT",
    body: JSON.stringify({ edits, rejectedFields, ...(changeNote ? { changeNote } : {}) }),
  });

export const approveBusinessProfile = (token: string, tenantId: string) =>
  request<BusinessProfileRecord>("/api/v1/business-discovery/approve", token, tenantId, { method: "POST" });

export const loadBusinessContext = (token: string, tenantId: string) =>
  request<{ profile: BusinessProfileRecord | null }>("/api/v1/business-discovery/context", token, tenantId);

// ---- Market Research ----
// Re-frames Business Discovery's already-approved market/ICP/competitive
// facts as a market-research brief -- no independent market sizing or
// competitor discovery exists anywhere in Clintra, so `limitation` is a
// permanent, honest disclosure, never a temporary placeholder.
export type MarketResearchBrief = {
  available: boolean;
  reason: string | null;
  profileStatus: "draft" | "approved" | null;
  profileUpdatedAt: string | null;
  market: BusinessFact[];
  icp: BusinessFact[];
  competitive: BusinessFact[];
  openQuestions: Gap[];
  researchQuestions: string[];
  limitation: string;
  overallConfidence: number | null;
};

export const loadMarketResearchBrief = (token: string, tenantId: string) =>
  request<MarketResearchBrief>("/api/v1/market-research/brief", token, tenantId);

export const narrateMarketResearchBrief = (token: string, tenantId: string) =>
  request<{ narrative: string | null; available: boolean }>("/api/v1/market-research/narrative", token, tenantId, { method: "POST" });

// ---- Social Presence ----
// The bare-minimum honest shell: reports only whether a real LinkedIn
// sign-in connection exists (LinkedIn's own integration only verifies
// sign-in, never posts/engagement) plus a permanent, honest disclosure of
// what cannot be measured -- never a fabricated cadence/engagement/reach number.
export type SocialPresenceSourceCategory = "linkedin_connection" | "social_listening" | "owned_website_social_links";
export type SocialPresenceSourceCoverageEntry = { source: SocialPresenceSourceCategory; available: boolean; reason: string | null };
export type SocialPresenceBrief = {
  linkedinConnected: boolean;
  linkedinLastSyncedAt: string | null;
  sourceCoverage: SocialPresenceSourceCoverageEntry[];
  unmeasurable: string[];
  limitation: string;
  recommendation: string;
};

export const loadSocialPresenceBrief = (token: string, tenantId: string) =>
  request<SocialPresenceBrief>("/api/v1/social-presence/brief", token, tenantId);

// ---- Content Generation ----
// Drafts campaign content against messaging pillars, brand voice, and
// audience/ICP context (read from an approved Business Profile). Every
// asset is a draft until a human explicitly approves it -- this agent never
// publishes, sends, or launches anything.

export type ContentType = "email" | "ad" | "landing_page" | "outreach";
export type ContentStatus = "draft" | "in_review" | "changes_requested" | "approved";
export type TransformOperation = "shorten" | "expand" | "change_tone" | "regenerate";

export type EmailContent = { subject: string; previewText: string; body: string; cta: string };
export type AdContent = { platform: string; headline: string; primaryText: string; cta: string };
export type LandingPageContent = { heroHeadline: string; subheadline: string; benefits: string[]; cta: string; supportingSections: string[] };
export type OutreachContent = { channel: string; persona: string; message: string; cta: string };
export type VariantContentValue = EmailContent | AdContent | LandingPageContent | OutreachContent;

export type PillarSnapshot = { id: string; pillar: string };

export type BrandVoiceProfile = {
  configured: boolean;
  traits: string[];
  doList: string[];
  avoidList: string[];
  source: "configured" | "website_scan" | "none";
};

export type AudienceSnapshot = {
  available: boolean;
  segment: string | null;
  persona: string | null;
  priorities: string[];
};

export type GenerationContext = {
  schemaVersion: 1;
  campaignId: string;
  campaignName: string;
  campaignObjective: string | null;
  pillarsUsed: PillarSnapshot[];
  brandVoiceSnapshot: BrandVoiceProfile;
  audienceSnapshot: AudienceSnapshot;
  contentType: ContentType;
  channel: string | null;
  tone: string | null;
  length: string | null;
  variantCount: number;
  cta: string | null;
  instructions: string | null;
  llmAvailable: boolean;
};

export type ActivityStep = { key: string; label: string; done: boolean };
export type RecommendationSeverity = "high" | "medium" | "low";
export type ContentRecommendation = { key: string; message: string; severity: RecommendationSeverity };

export type CampaignSummary = {
  id: string;
  name: string;
  objective: string | null;
  targetSegment: string | null;
  pillarCount: number;
  createdAt: string;
};
export type CampaignDetail = CampaignSummary & {
  pillars: PillarSnapshot[];
  draftCountByType: Partial<Record<ContentType, number>>;
};

export type ContentVariantRecord = {
  id: string;
  draftId: string;
  label: string;
  content: VariantContentValue;
  isEdited: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ContentDraftRecord = {
  id: string;
  campaignId: string;
  contentType: ContentType;
  channel: string | null;
  name: string;
  status: ContentStatus;
  generationContext: GenerationContext;
  selectedVariantId: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  changeNote: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export type GenerateDraftResult = {
  draft: ContentDraftRecord;
  variants: ContentVariantRecord[];
  activity: ActivityStep[];
  recommendations: ContentRecommendation[];
};

export const loadCampaigns = (token: string, tenantId: string) => request<{ data: CampaignSummary[] }>("/api/v1/content-generation/campaigns", token, tenantId);

export const createCampaign = (token: string, tenantId: string, input: { name: string; objective?: string; targetSegment?: string; pillars: string[] }) =>
  request<CampaignDetail>("/api/v1/content-generation/campaigns", token, tenantId, { method: "POST", body: JSON.stringify(input) });

export const getCampaign = (token: string, tenantId: string, campaignId: string) =>
  request<CampaignDetail>(`/api/v1/content-generation/campaigns/${encodeURIComponent(campaignId)}`, token, tenantId);

export const loadBrandVoice = (token: string, tenantId: string) => request<BrandVoiceProfile>("/api/v1/content-generation/brand-voice", token, tenantId);

export const saveBrandVoice = (token: string, tenantId: string, input: { traits: string[]; doList: string[]; avoidList: string[] }) =>
  request<BrandVoiceProfile>("/api/v1/content-generation/brand-voice", token, tenantId, { method: "PUT", body: JSON.stringify(input) });

export const loadAudienceContext = (token: string, tenantId: string) => request<AudienceSnapshot>("/api/v1/content-generation/audience", token, tenantId);

export const generateContentDraft = (
  token: string,
  tenantId: string,
  input: { campaignId: string; contentType: ContentType; name: string; channel?: string; tone?: string; length?: string; variantCount: number; cta?: string; instructions?: string },
) => request<GenerateDraftResult>("/api/v1/content-generation/drafts/generate", token, tenantId, { method: "POST", body: JSON.stringify(input) });

export const loadContentDrafts = (token: string, tenantId: string, status?: ContentStatus) =>
  request<{ data: ContentDraftRecord[] }>(`/api/v1/content-generation/drafts${status ? `?status=${encodeURIComponent(status)}` : ""}`, token, tenantId);

export const getContentDraft = (token: string, tenantId: string, draftId: string) =>
  request<{ draft: ContentDraftRecord; variants: ContentVariantRecord[] }>(`/api/v1/content-generation/drafts/${encodeURIComponent(draftId)}`, token, tenantId);

export const transformContentVariant = (token: string, tenantId: string, variantId: string, operation: TransformOperation) =>
  request<ContentVariantRecord>(`/api/v1/content-generation/variants/${encodeURIComponent(variantId)}/transform`, token, tenantId, { method: "POST", body: JSON.stringify({ operation }) });

export const updateContentVariant = (token: string, tenantId: string, variantId: string, content: VariantContentValue) =>
  request<ContentVariantRecord>(`/api/v1/content-generation/variants/${encodeURIComponent(variantId)}`, token, tenantId, { method: "PUT", body: JSON.stringify({ content }) });

export const undoContentVariant = (token: string, tenantId: string, variantId: string) =>
  request<ContentVariantRecord>(`/api/v1/content-generation/variants/${encodeURIComponent(variantId)}/undo`, token, tenantId, { method: "POST" });

export const duplicateContentVariant = (token: string, tenantId: string, variantId: string) =>
  request<ContentVariantRecord>(`/api/v1/content-generation/variants/${encodeURIComponent(variantId)}/duplicate`, token, tenantId, { method: "POST" });

export const selectContentVariant = (token: string, tenantId: string, draftId: string, variantId: string) =>
  request<ContentDraftRecord>(`/api/v1/content-generation/drafts/${encodeURIComponent(draftId)}/select-variant`, token, tenantId, { method: "POST", body: JSON.stringify({ variantId }) });

export const submitDraftForReview = (token: string, tenantId: string, draftId: string) =>
  request<ContentDraftRecord>(`/api/v1/content-generation/drafts/${encodeURIComponent(draftId)}/submit`, token, tenantId, { method: "POST" });

export const decideContentDraft = (token: string, tenantId: string, draftId: string, decision: "approved" | "changes_requested", note?: string) =>
  request<ContentDraftRecord>(`/api/v1/content-generation/drafts/${encodeURIComponent(draftId)}/decision`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ decision, ...(note ? { note } : {}) }),
  });

// ---- Outreach Personalisation ----
// Per-lead, evidence-grounded draft generation -- distinct from Content
// Generation's own generic 'outreach' content type, which is a campaign-level
// template with no real lead behind it.
export type OutreachChannel = "email" | "linkedin_message";
export type OutreachDraftStatus = "draft" | "in_review" | "changes_requested" | "approved";
export type OutreachConsentStatus = "granted" | "revoked" | "unknown";

export type OutreachGenerationContext = {
  schemaVersion: 1;
  lead: { firstName: string | null; lastName: string | null; title: string | null; stage: string; source: string | null; seniority: string | null };
  account: { available: boolean; name: string | null; industry: string | null; employeeBand: string | null; revenueBand: string | null };
  leadScore: { available: boolean; score: number | null; band: string | null; explanation: string[] };
  buyingIntent: { available: boolean; band: "low" | "medium" | "high" | null; trend: string | null; signalCount: number; topSignals: Array<{ kind: string; source: string; strength: number; occurredAt: string }> };
  recentActivity: Array<{ kind: string; source: string; occurredAt: string }>;
  consentStatus: OutreachConsentStatus;
  channel: OutreachChannel;
  instructions: string | null;
  llmAvailable: boolean;
};

export type OutreachDraftRecord = {
  id: string;
  leadId: string;
  accountId: string | null;
  channel: OutreachChannel;
  subject: string | null;
  body: string;
  status: OutreachDraftStatus;
  generationContext: OutreachGenerationContext;
  decidedBy: string | null;
  decidedAt: string | null;
  changeNote: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export const generateOutreachDraft = (token: string, tenantId: string, leadId: string, input: { channel?: OutreachChannel; instructions?: string } = {}) =>
  request<OutreachDraftRecord>(`/api/v1/outreach-personalisation/leads/${encodeURIComponent(leadId)}/drafts`, token, tenantId, { method: "POST", body: JSON.stringify(input) });

export const loadOutreachDrafts = (token: string, tenantId: string, status?: OutreachDraftStatus) =>
  request<{ data: OutreachDraftRecord[] }>(`/api/v1/outreach-personalisation/drafts${status ? `?status=${encodeURIComponent(status)}` : ""}`, token, tenantId);

export const getOutreachDraft = (token: string, tenantId: string, draftId: string) =>
  request<OutreachDraftRecord>(`/api/v1/outreach-personalisation/drafts/${encodeURIComponent(draftId)}`, token, tenantId);

export const updateOutreachDraft = (token: string, tenantId: string, draftId: string, patch: { subject?: string | null; body?: string }) =>
  request<OutreachDraftRecord>(`/api/v1/outreach-personalisation/drafts/${encodeURIComponent(draftId)}`, token, tenantId, { method: "PUT", body: JSON.stringify(patch) });

export const submitOutreachDraftForReview = (token: string, tenantId: string, draftId: string) =>
  request<OutreachDraftRecord>(`/api/v1/outreach-personalisation/drafts/${encodeURIComponent(draftId)}/submit`, token, tenantId, { method: "POST" });

export const decideOutreachDraft = (token: string, tenantId: string, draftId: string, decision: "approved" | "changes_requested", note?: string) =>
  request<OutreachDraftRecord>(`/api/v1/outreach-personalisation/drafts/${encodeURIComponent(draftId)}/decision`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ decision, ...(note ? { note } : {}) }),
  });

// ---- Marketing Strategy ----
// Synthesizes verified Business Discovery context, Website Intelligence
// findings, real business goals, and Revenue Orchestration signals into a
// prioritized marketing strategy draft. It never executes anything -- every
// initiative requires human accept/reject and the whole strategy requires
// explicit approval before it can be handed toward Campaign Planning.

export type ImpactLevel = "high" | "medium" | "low";
export type EffortLevel = "high" | "medium" | "low";
export type RoadmapPhase = "now" | "next" | "later";
export type StrategyStatus = "draft" | "approved";
export type InitiativeStatus = "pending" | "accepted" | "rejected";

export type AudienceInsight = {
  audience: string;
  buyerRole: string;
  need: string;
  relevantOffering: string;
  rationale: string;
  evidence: string[];
  confidence: ConfidenceLevel;
};

export type PositioningDirection = {
  corePositioning: string;
  messageThemes: string[];
  differentiation: string[];
  messagingGaps: string[];
};

export type ChannelStrategyEntry = {
  channel: string;
  purpose: string;
  audience: string;
  strategicRole: string;
  rationale: string;
  confidence: ConfidenceLevel;
};

export type StrategySummary = {
  currentSituation: string;
  primaryChallenge: string;
  biggestOpportunity: string;
  strategicDirection: string;
};

export type RevenueTargetSnapshot = { amount: number; currency: string; fiscalYear: number };
export type GoalsSnapshot = { primaryGoal: string | null; revenueTarget: RevenueTargetSnapshot | null };

export type BusinessFactSnapshot = { field: string; label: string; value: string };
export type WebsiteFindingSnapshot = { category: string; severity: string; title: string; description: string };
export type WebsiteRecommendationSnapshot = { category: string; priority: string; what: string; why: string };
export type GapDriverSnapshot = { title: string; description: string; severity: string };
export type OptimisationSignalSnapshot = { sourceAgent: "performance-optimisation" | "conversion-optimisation"; title: string; rationale: string; confidence: number | null; decidedAt: string };

export type StrategyGenerationContext = {
  schemaVersion: 1;
  businessFacts: {
    company: BusinessFactSnapshot[];
    offering: BusinessFactSnapshot[];
    market: BusinessFactSnapshot[];
    icp: BusinessFactSnapshot[];
    positioning: BusinessFactSnapshot[];
    competitive: BusinessFactSnapshot[];
  };
  businessContextAvailable: boolean;
  websiteFindings: WebsiteFindingSnapshot[];
  websiteRecommendations: WebsiteRecommendationSnapshot[];
  websiteIntelligenceAvailable: boolean;
  goals: GoalsSnapshot;
  gapDrivers: GapDriverSnapshot[];
  revenueOrchestrationAvailable: boolean;
  optimisationSignals: OptimisationSignalSnapshot[];
  optimisationSignalsAvailable: boolean;
  llmAvailable: boolean;
};

export type EvidenceChecklistItem = { key: string; label: string; done: boolean };
export type MissingInfoSource = "structural" | "ai_identified";
export type MissingInfoItem = { text: string; source: MissingInfoSource };

export type StrategyRecord = {
  id: string;
  revision: number;
  status: StrategyStatus;
  strategySummary: StrategySummary;
  goals: GoalsSnapshot;
  priorityAudiences: AudienceInsight[];
  positioning: PositioningDirection;
  channelStrategy: ChannelStrategyEntry[];
  missingInformation: MissingInfoItem[];
  overallConfidence: ConfidenceLevel | null;
  generationContext: StrategyGenerationContext;
  generatedBy: "ai" | "human_edited";
  updatedBy: string | null;
  changeNote: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export type InitiativeRecord = {
  id: string;
  strategyId: string;
  rank: number;
  name: string;
  objective: string;
  audience: string;
  impact: ImpactLevel;
  effort: EffortLevel;
  confidence: ConfidenceLevel;
  roadmapPhase: RoadmapPhase;
  priorityScore: number;
  scoringVersion: number;
  rationale: string;
  evidence: string[];
  dependencies: string[];
  nextStep: string;
  status: InitiativeStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  sentToCampaignPlanning: boolean;
  campaignId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type GetStrategyResult = { strategy: StrategyRecord | null; initiatives: InitiativeRecord[]; sentElsewhere: InitiativeRecord[]; evidenceChecklist: EvidenceChecklistItem[] };
export type GenerateStrategyResult = { strategy: StrategyRecord; initiatives: InitiativeRecord[]; evidenceChecklist: EvidenceChecklistItem[] };
export type SendToCampaignPlanningResult = { initiative: InitiativeRecord; campaignId: string; campaignName: string; disclosure: string };

export const loadMarketingStrategy = (token: string, tenantId: string) => request<GetStrategyResult>("/api/v1/marketing-strategy", token, tenantId);

export const generateMarketingStrategy = (token: string, tenantId: string) =>
  request<GenerateStrategyResult>("/api/v1/marketing-strategy/generate", token, tenantId, { method: "POST" });

export const editStrategyInitiative = (
  token: string,
  tenantId: string,
  initiativeId: string,
  edits: Partial<{ name: string; objective: string; audience: string; rationale: string; nextStep: string }>,
) => request<InitiativeRecord>(`/api/v1/marketing-strategy/initiatives/${encodeURIComponent(initiativeId)}`, token, tenantId, { method: "PUT", body: JSON.stringify(edits) });

export const decideStrategyInitiative = (token: string, tenantId: string, initiativeId: string, decision: "accepted" | "rejected", note?: string) =>
  request<InitiativeRecord>(`/api/v1/marketing-strategy/initiatives/${encodeURIComponent(initiativeId)}/decision`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ decision, ...(note ? { note } : {}) }),
  });

export const approveMarketingStrategy = (token: string, tenantId: string) =>
  request<StrategyRecord>("/api/v1/marketing-strategy/approve", token, tenantId, { method: "POST" });

export const sendInitiativeToCampaignPlanning = (token: string, tenantId: string, initiativeId: string) =>
  request<SendToCampaignPlanningResult>(`/api/v1/marketing-strategy/initiatives/${encodeURIComponent(initiativeId)}/send-to-campaign-planning`, token, tenantId, { method: "POST" });

// ---- Sales Planning ----
// Translates an approved revenue target into a territory/account coverage
// plan using real CRM data, Lead Intelligence's ICP-fit scoring, and real
// synced activity mapped into Buying Intent's scoring function. Coverage and
// account tiering are always live-computed from current CRM state; only AI
// recommendations and the territory structure are persisted. Never
// auto-finalizes territory assignments, reassigns reps, or changes CRM
// ownership/quotas/compensation.

export type FitBand = "high" | "medium" | "low" | null;
export type IntentBand = "high" | "medium" | "low" | "insufficient_signal";
export type AccountTier = "tier_1" | "tier_2" | "tier_3" | "tier_4";
export type GapCertainty = "known" | "potential_risk";
export type TerritoryStatus = "proposed" | "approved";

export type AccountIntent = {
  available: boolean;
  band: IntentBand;
  score: number | null;
  unavailableSignalKinds: string[];
};

export type AccountPlanSummary = {
  id: string;
  name: string;
  industry: string | null;
  employeeBand: string | null;
  revenueBand: string | null;
  ownerUserId: string | null;
  territoryId: string | null;
  openPipelineAmount: number;
  hasNearTermOpportunity: boolean;
  hasExistingRelationship: boolean;
  fitBand: FitBand;
  scoredLeadCount: number;
  intent: AccountIntent;
  tier: AccountTier;
  tierReasons: string[];
};

export type TerritoryRecord = {
  id: string | null;
  name: string;
  description: string | null;
  basis: "segment" | "manual";
  status: TerritoryStatus;
  accountCount: number;
  openPipelineAmount: number;
  allocatedTarget: number | null;
  priorityAccountCount: number;
  highIntentAccountCount: number;
};

export type CoverageGap = {
  key: string;
  scope: "overall" | "territory" | "account";
  certainty: GapCertainty;
  severity: "critical" | "high" | "medium" | "low";
  title: string;
  target: number | null;
  currentCoverage: number | null;
  shortfall: number | null;
  reason: string;
  recommendedResponse: string;
};

export type DealRiskType = "no_close_date" | "overdue" | "no_recent_activity";
export type DealRiskFinding = {
  key: string;
  opportunityId: string;
  opportunityName: string;
  accountName: string | null;
  ownerUserId: string | null;
  type: DealRiskType;
  severity: "high" | "medium" | "low";
  title: string;
  description: string;
};

export type PlanOverview = {
  target: number | null;
  currentPipeline: number;
  coveragePercent: number | null;
  revenueGap: number | null;
  pipelineRequired: number | null;
  priorityAccountCount: number;
  coverageGapCount: number;
  status: "no_target" | "on_track" | "at_risk" | "insufficient_data";
};

export type SalesRecommendationRecord = {
  id: string;
  actionType: string;
  title: string;
  rationale: string;
  evidence: string[];
  expectedImpact: string;
  confidence: ConfidenceLevel;
  accountId: string | null;
  status: "pending" | "approved" | "rejected" | "expired";
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
};

export type SalesPlanResult = {
  overview: PlanOverview;
  territories: TerritoryRecord[];
  accounts: AccountPlanSummary[];
  coverageGaps: CoverageGap[];
  dealRisks: DealRiskFinding[];
  recommendations: SalesRecommendationRecord[];
};

export const loadSalesPlan = (token: string, tenantId: string) => request<SalesPlanResult>("/api/v1/sales-planning/plan", token, tenantId);

export const generateSalesRecommendations = (token: string, tenantId: string) =>
  request<{ data: SalesRecommendationRecord[] }>("/api/v1/sales-planning/recommendations/generate", token, tenantId, { method: "POST" });

export const decideSalesRecommendation = (token: string, tenantId: string, recommendationId: string, decision: "approved" | "rejected", reason?: string) =>
  request<SalesRecommendationRecord>(`/api/v1/sales-planning/recommendations/${encodeURIComponent(recommendationId)}/decision`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ decision, ...(reason ? { reason } : {}) }),
  });

export const applyProposedTerritoryStructure = (token: string, tenantId: string) =>
  request<{ data: TerritoryRecord[] }>("/api/v1/sales-planning/territories/apply-proposed", token, tenantId, { method: "POST" });

export const approveSalesTerritory = (token: string, tenantId: string, territoryId: string) =>
  request<TerritoryRecord>(`/api/v1/sales-planning/territories/${encodeURIComponent(territoryId)}/approve`, token, tenantId, { method: "POST" });

export const reassignAccountTerritory = (token: string, tenantId: string, accountId: string, territoryId: string | null) =>
  request<AccountPlanSummary>(`/api/v1/sales-planning/accounts/${encodeURIComponent(accountId)}/territory`, token, tenantId, { method: "PUT", body: JSON.stringify({ territoryId }) });

// ---- Campaign Planning ----
// Turns an approved Marketing Strategy initiative (or an independent,
// user-created campaign idea) into a structured, multi-channel campaign
// blueprint -- channels, phases, timeline, messaging pillars, and asset
// requirements for Content Generation to draft against. It never publishes,
// schedules, or launches anything; every plan requires human approval
// before it can be sent to Content Generation.

export type CampaignPlanStatus = "draft" | "needs_review" | "approved" | "sent_to_content_generation";
export type CampaignGeneratedBy = "ai" | "human_edited" | "user_created";
export type CampaignAssetStatus = "required" | "ready_for_content_generation" | "drafting" | "drafted" | "approved";
export type CampaignPriority = "high" | "medium" | "low";
export type CampaignRegenerateSection = "concept" | "channels" | "phases_timeline" | "messaging_pillars" | "assets";

export type PlanCampaignAudience = { primary: string; secondary: string | null; characteristics: string[]; painPoints: string[] };
export type ChannelPlanEntry = {
  channel: string;
  role: string;
  purpose: string;
  audience: string;
  phase: string;
  message: string;
  cta: string;
  requiredAssets: string[];
  dependencies: string[];
};
export type CampaignPhase = { name: string; description: string };
export type TimelineActivity = { label: string; dayOffset: number; phase: string };
export type CampaignKpi = { name: string; target: string | null };
export type CampaignRecommendation = { title: string; reasons: string[]; confidence: ConfidenceLevel };

export type CampaignPlanSummary = {
  id: string;
  name: string;
  objective: string | null;
  targetSegment: string | null;
  planStatus: CampaignPlanStatus;
  strategicInitiativeId: string | null;
  createdAt: string;
};

export type CampaignPlanRecord = CampaignPlanSummary & {
  budget: number | null;
  startsAt: string | null;
  endsAt: string | null;
  concept: string | null;
  audience: PlanCampaignAudience;
  channels: ChannelPlanEntry[];
  phases: CampaignPhase[];
  timeline: TimelineActivity[];
  kpis: CampaignKpi[];
  dependencies: string[];
  assumptions: string[];
  generatedBy: CampaignGeneratedBy;
  updatedBy: string | null;
  changeNote: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  createdBy: string | null;
  updatedAt: string;
};

export type MessagingPillarRecord = {
  id: string;
  campaignId: string;
  pillar: string;
  position: number;
  coreMessage: string | null;
  audienceProblem: string | null;
  supportingContext: string | null;
  recommendedUsage: string | null;
  createdAt: string;
};

export type AssetRequirementRecord = {
  id: string;
  campaignId: string;
  assetType: string;
  channel: string;
  phase: string | null;
  purpose: string;
  messagingPillarId: string | null;
  cta: string | null;
  audience: string | null;
  priority: CampaignPriority;
  dependencies: string[];
  status: CampaignAssetStatus;
  contentDraftId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CampaignPlanDetail = { campaign: CampaignPlanRecord; pillars: MessagingPillarRecord[]; assets: AssetRequirementRecord[] };
export type GeneratePlanResult = CampaignPlanDetail & { recommendations: CampaignRecommendation[] };
export type AcceptedInitiativeSummary = { id: string; name: string; objective: string; audience: string; rationale: string; campaignId: string | null };

export const loadCampaignPlans = (token: string, tenantId: string) => request<{ data: CampaignPlanSummary[] }>("/api/v1/campaign-planning/campaigns", token, tenantId);

export const createCampaignPlan = (token: string, tenantId: string, input: { name: string; objective?: string; targetSegment?: string; pillars: string[] }) =>
  request<CampaignPlanDetail>("/api/v1/campaign-planning/campaigns", token, tenantId, { method: "POST", body: JSON.stringify(input) });

export const getCampaignPlan = (token: string, tenantId: string, campaignId: string) =>
  request<CampaignPlanDetail>(`/api/v1/campaign-planning/campaigns/${encodeURIComponent(campaignId)}`, token, tenantId);

export const loadAcceptedInitiatives = (token: string, tenantId: string) => request<{ data: AcceptedInitiativeSummary[] }>("/api/v1/campaign-planning/initiatives", token, tenantId);

export const startCampaignFromInitiative = (token: string, tenantId: string, initiativeId: string) =>
  request<CampaignPlanDetail>("/api/v1/campaign-planning/campaigns/from-initiative", token, tenantId, { method: "POST", body: JSON.stringify({ initiativeId }) });

export const generateCampaignPlan = (token: string, tenantId: string, campaignId: string) =>
  request<GeneratePlanResult>(`/api/v1/campaign-planning/campaigns/${encodeURIComponent(campaignId)}/generate`, token, tenantId, { method: "POST" });

export const regenerateCampaignSection = (token: string, tenantId: string, campaignId: string, section: CampaignRegenerateSection) =>
  request<CampaignPlanDetail>(`/api/v1/campaign-planning/campaigns/${encodeURIComponent(campaignId)}/regenerate-section`, token, tenantId, { method: "POST", body: JSON.stringify({ section }) });

export const editCampaignPlan = (
  token: string,
  tenantId: string,
  campaignId: string,
  edits: Partial<{ name: string; objective: string; concept: string; audience: PlanCampaignAudience; channels: ChannelPlanEntry[]; phases: CampaignPhase[]; timeline: TimelineActivity[]; kpis: CampaignKpi[]; dependencies: string[] }>,
) => request<CampaignPlanRecord>(`/api/v1/campaign-planning/campaigns/${encodeURIComponent(campaignId)}`, token, tenantId, { method: "PUT", body: JSON.stringify(edits) });

export const approveCampaignPlan = (token: string, tenantId: string, campaignId: string) =>
  request<CampaignPlanRecord>(`/api/v1/campaign-planning/campaigns/${encodeURIComponent(campaignId)}/approve`, token, tenantId, { method: "POST" });

export const sendCampaignToContentGeneration = (token: string, tenantId: string, campaignId: string) =>
  request<CampaignPlanDetail>(`/api/v1/campaign-planning/campaigns/${encodeURIComponent(campaignId)}/send-to-content-generation`, token, tenantId, { method: "POST" });

export const updateAssetRequirementStatus = (token: string, tenantId: string, requirementId: string, status: CampaignAssetStatus) =>
  request<AssetRequirementRecord>(`/api/v1/campaign-planning/asset-requirements/${encodeURIComponent(requirementId)}`, token, tenantId, { method: "PUT", body: JSON.stringify({ status }) });

// ---- Performance Optimisation ----
// Closes the Plan -> Execute -> Measure -> Analyze -> Learn -> Recommend
// loop. There is no real tracked campaign/content/channel EXECUTION
// performance data in Clintra (nothing publishes or sends), so every
// section below is honestly scoped to what's real: pipeline/revenue,
// win-loss and lead-conversion trends, Clintra's own planning-pipeline
// throughput, segment performance, and (when connected) site-wide Google
// Analytics traffic. Unavailable sections are always present with
// {available:false, reason}, never silently omitted or fabricated.

export type OverallHealth = "on_track" | "needs_attention" | "at_risk" | "insufficient_data";
export type DestinationAgent = "marketing-strategy" | "campaign-planning" | "sales-planning" | "revenue-orchestration" | "content-generation" | "revenue-forecasting";
export type Certainty = "known" | "hypothesis";
export type RecommendationCategory = "continue" | "improve" | "test" | "investigate" | "reconsider";

export type PeriodWindow = { label: string; start: string; end: string; days: number };
export type ComparisonPeriod = { current: PeriodWindow; previous: PeriodWindow; widened: boolean };

export type WinLossTrend = {
  available: boolean;
  reason: string | null;
  currentWinRate: number | null;
  previousWinRate: number | null;
  currentClosedCount: number;
  previousClosedCount: number;
  changePct: number | null;
};

export type LeadConversionTrend = {
  available: boolean;
  reason: string | null;
  currentTotal: number;
  previousTotal: number;
  currentConverted: number;
  previousConverted: number;
  currentRate: number | null;
  previousRate: number | null;
  changePct: number | null;
};

export type PipelineSnapshot = {
  available: boolean;
  reason: string | null;
  target: number | null;
  currentRevenue: number | null;
  pipeline: number | null;
  forecast: number | null;
  gap: number | null;
  health: string | null;
};

export type PlanningThroughput = {
  available: boolean;
  reason: string | null;
  initiativesAccepted: number;
  initiativesRejected: number;
  campaignsByPlanStatus: Record<string, number>;
  contentDraftsByStatus: Record<string, number>;
  coverageGapCount: number;
  unassignedPriorityAccountCount: number;
};

export type SegmentPerformance = {
  segmentLabel: string;
  dimension: "territory" | "opportunity_source";
  winRate: number | null;
  closedCount: number;
  openPipelineAmount: number;
};

export type WebsiteTrafficSnapshot = {
  available: boolean;
  reason: string | null;
  sessions: number | null;
  activeUsers: number | null;
  conversions: number | null;
  windowDays: number;
};

export type RootCauseHypothesis = { cause: string; confidence: ConfidenceLevel };
export type QuarterOverQuarterQualifier = { available: boolean; reason: string | null; changePct: number | null; sustained: boolean | null };

export type UnderperformanceFinding = {
  key: string;
  title: string;
  metric: string;
  currentValue: string;
  comparison: string;
  change: string;
  certainty: Certainty;
  rootCauseHypotheses: RootCauseHypothesis[];
  evidence: string[];
  quarterOverQuarter: QuarterOverQuarterQualifier | null;
};

export type PatternFinding = { key: string; title: string; description: string; evidence: string[] };
export type DataQualityNote = { text: string; source: "structural" | "ai_identified" };

export type PerformanceRecommendation = {
  id: string;
  title: string;
  description: string;
  reason: string;
  evidence: string[];
  priority: "high" | "medium" | "low";
  confidence: ConfidenceLevel;
  targetArea: string;
  destinationAgent: DestinationAgent | null;
  category: RecommendationCategory | null;
  status: "pending" | "approved" | "rejected" | "expired";
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
};

export type AgentFeedbackGroup = { summary: string; findingTitles: string[] };

export type PerformanceAnalysisResult = {
  hasRun: true;
  runId: string;
  startedAt: string;
  overallHealth: OverallHealth;
  period: ComparisonPeriod;
  pipeline: PipelineSnapshot;
  winLoss: WinLossTrend;
  leadConversion: LeadConversionTrend;
  planningThroughput: PlanningThroughput;
  segmentPerformance: SegmentPerformance[];
  websiteTraffic: WebsiteTrafficSnapshot;
  underperformance: UnderperformanceFinding[];
  patterns: PatternFinding[];
  dataQualityNotes: DataQualityNote[];
  recommendations: PerformanceRecommendation[];
  recommendedAgentFeedback: Partial<Record<DestinationAgent, AgentFeedbackGroup>>;
  unassignedFeedback: AgentFeedbackGroup | null;
};
export type NoPerformanceAnalysisYet = { hasRun: false };

export type RecommendationOutcome = {
  recommendationId: string;
  title: string;
  category: RecommendationCategory | null;
  baselineRunAt: string;
  available: boolean;
  reason: string | null;
  overallHealthBefore: OverallHealth | null;
  overallHealthAfter: OverallHealth | null;
  winRateBefore: number | null;
  winRateAfter: number | null;
  leadConversionRateBefore: number | null;
  leadConversionRateAfter: number | null;
};

export type PerformanceAnalysisHistoryEntry = {
  runId: string;
  startedAt: string;
  periodLabel: string;
  overallHealth: OverallHealth;
  underperformanceCount: number;
  recommendationCount: number;
  recommendations: PerformanceRecommendation[];
};

export const loadPerformanceAnalysis = (token: string, tenantId: string) =>
  request<PerformanceAnalysisResult | NoPerformanceAnalysisYet>("/api/v1/performance-optimisation/analysis", token, tenantId);

export const runPerformanceAnalysis = (token: string, tenantId: string) =>
  request<PerformanceAnalysisResult>("/api/v1/performance-optimisation/analysis/run", token, tenantId, { method: "POST" });

export const loadPerformanceAnalysisHistory = (token: string, tenantId: string) =>
  request<{ data: PerformanceAnalysisHistoryEntry[] }>("/api/v1/performance-optimisation/analysis/history", token, tenantId);

export const decidePerformanceRecommendation = (token: string, tenantId: string, id: string, decision: "approved" | "rejected", reason?: string) =>
  request<PerformanceRecommendation>(`/api/v1/performance-optimisation/recommendations/${encodeURIComponent(id)}/decision`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ decision, ...(reason ? { reason } : {}) }),
  });

export const loadRecommendationOutcomes = (token: string, tenantId: string) =>
  request<{ data: RecommendationOutcome[] }>("/api/v1/performance-optimisation/recommendations/outcomes", token, tenantId);

export type BuyingIntentTrend = "rising" | "stable" | "falling" | "inactive" | "insufficient_data";

export type IntentSourceCoverageEntry = { source: string; available: boolean };
export type IntentTopSignal = { kind: string; source: string; strength: number; occurredAt: string };
export type IntentAccountExplanation = {
  accountName: string;
  summary: string;
  drivers: string[];
  trendExplanation: string;
  recommendedAction: string;
  confidence: ConfidenceLevel;
};

export type BuyingIntentAccountSnapshot = {
  accountId: string;
  accountName: string;
  score: number | null;
  band: IntentBand;
  confidence: number | null;
  signalCount: number;
  sourceCoverage: IntentSourceCoverageEntry[];
  topSignals: IntentTopSignal[];
  unavailableSignalKinds: string[];
  previousScore: number | null;
  change: number | null;
  trend: BuyingIntentTrend;
  snapshotAt: string;
  explanation: IntentAccountExplanation | null;
};

export type BuyingIntentOverview = {
  hasRun: boolean;
  lastRunAt: string | null;
  highIntentCount: number;
  risingCount: number;
  fallingCount: number;
  newlyActiveCount: number;
  scoredAccountCount: number;
};

export type BuyingIntentRunResult = BuyingIntentOverview & {
  hasRun: true;
  runId: string;
  accounts: BuyingIntentAccountSnapshot[];
  alertsCreated: number;
};

export type BuyingIntentAccountDetail = {
  accountId: string;
  accountName: string;
  snapshot: BuyingIntentAccountSnapshot | null;
};

export type BuyingIntentSignalTimelineEntry = { label: string; kind: string; source: string; strength: number; occurredAt: string };
export type BuyingIntentHistoryEntry = { score: number | null; band: IntentBand; confidence: number | null; trend: BuyingIntentTrend; snapshotAt: string };

export type BuyingIntentExplainResult = { available: boolean; reason: string | null; explanation: IntentAccountExplanation | null };

export type BuyingIntentAlert = {
  id: string;
  accountId: string | null;
  accountName: string | null;
  title: string;
  rationale: string;
  evidence: string[];
  confidence: number | null;
  status: "pending" | "approved" | "rejected" | "expired";
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
};

export type BuyingIntentAccountListQuery = { intentLevel?: IntentBand; trend?: BuyingIntentTrend; sort?: "score_desc" | "score_asc" | "recent_activity" };

const buyingIntentQuery = (query: BuyingIntentAccountListQuery) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value));
  return params.size ? `?${params}` : "";
};

export const loadBuyingIntentOverview = (token: string, tenantId: string) =>
  request<BuyingIntentOverview>("/api/v1/buying-intent/overview", token, tenantId);

export const runBuyingIntentAnalysis = (token: string, tenantId: string) =>
  request<BuyingIntentRunResult>("/api/v1/buying-intent/run", token, tenantId, { method: "POST" });

export const loadBuyingIntentAccounts = (token: string, tenantId: string, query: BuyingIntentAccountListQuery = {}) =>
  request<{ data: BuyingIntentAccountSnapshot[] }>(`/api/v1/buying-intent/accounts${buyingIntentQuery(query)}`, token, tenantId);

export const loadBuyingIntentAccountDetail = (token: string, tenantId: string, accountId: string) =>
  request<BuyingIntentAccountDetail>(`/api/v1/buying-intent/accounts/${encodeURIComponent(accountId)}`, token, tenantId);

export const loadBuyingIntentAccountSignals = (token: string, tenantId: string, accountId: string) =>
  request<{ data: BuyingIntentSignalTimelineEntry[] }>(`/api/v1/buying-intent/accounts/${encodeURIComponent(accountId)}/signals`, token, tenantId);

export const loadBuyingIntentAccountHistory = (token: string, tenantId: string, accountId: string) =>
  request<{ data: BuyingIntentHistoryEntry[] }>(`/api/v1/buying-intent/accounts/${encodeURIComponent(accountId)}/history`, token, tenantId);

export const explainBuyingIntentAccount = (token: string, tenantId: string, accountId: string) =>
  request<BuyingIntentExplainResult>(`/api/v1/buying-intent/accounts/${encodeURIComponent(accountId)}/explain`, token, tenantId, { method: "POST" });

export const loadBuyingIntentAlerts = (token: string, tenantId: string) =>
  request<{ data: BuyingIntentAlert[] }>("/api/v1/buying-intent/alerts", token, tenantId);

export const decideBuyingIntentAlert = (token: string, tenantId: string, id: string, decision: "approved" | "rejected", reason?: string) =>
  request<BuyingIntentAlert>(`/api/v1/buying-intent/alerts/${encodeURIComponent(id)}/decision`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ decision, ...(reason ? { reason } : {}) }),
  });

export type NurtureClassification = "new" | "engaging" | "evaluating" | "high_intent" | "sales_ready" | "dormant" | "re_engagement_candidate" | "disqualified_suppress";
export type NurtureEngagementLevel = "high" | "medium" | "low" | "none";
export type NurtureEngagementTrend = "increasing" | "stable" | "declining" | "insufficient_data";

export type NurtureLeadExplanation = { leadName: string; summary: string; nextRecommendedAction: string; confidence: ConfidenceLevel };

export type NurtureLeadSnapshot = {
  leadId: string;
  leadName: string;
  classification: NurtureClassification;
  reasons: string[];
  engagementLevel: NurtureEngagementLevel;
  engagementTrend: NurtureEngagementTrend;
  trackId: string | null;
  trackName: string | null;
  currentBand: LeadBand | null;
  accountIntentBand: IntentBand | null;
  accountIntentTrend: BuyingIntentTrend | null;
  nextRecommendedAction: NurtureLeadExplanation | null;
  confidence: number | null;
  snapshotAt: string;
};

export type NurtureOverview = {
  hasRun: boolean;
  lastRunAt: string | null;
  leadsInNurtureCount: number;
  salesReadyCount: number;
  dormantCount: number;
  reEngagementCandidateCount: number;
  scoredLeadCount: number;
};

export type NurtureRunResult = NurtureOverview & { hasRun: true; runId: string; leads: NurtureLeadSnapshot[]; alertsCreated: number };

export type NurtureLeadDetail = { leadId: string; leadName: string; snapshot: NurtureLeadSnapshot | null };
export type NurtureLeadHistoryEntry = { classification: NurtureClassification; engagementLevel: NurtureEngagementLevel; engagementTrend: NurtureEngagementTrend; snapshotAt: string };
export type NurtureExplainResult = { available: boolean; reason: string | null; explanation: NurtureLeadExplanation | null };

export type NurtureAlert = {
  id: string;
  leadId: string | null;
  leadName: string | null;
  title: string;
  rationale: string;
  evidence: string[];
  confidence: number | null;
  status: "pending" | "approved" | "rejected" | "expired";
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
};

export type NurtureSegment = {
  segmentKey: string;
  classification: NurtureClassification;
  band: LeadBand | null;
  stageBucket: string;
  leadCount: number;
  sampleReasons: string[];
  accountIntentLevels: Partial<Record<string, number>>;
};

export type NurtureContentBrief = { audience: string; evidence: string; tone: string };
export type NurtureStepDraft = { subject: string; body: string; status: "draft" | "approved" | "rejected"; generatedAt: string };
export type NurtureStage = {
  stepNumber: number;
  channel: string;
  timing: string;
  purpose: string;
  topic: string;
  cta: string;
  progressionCondition: string;
  contentBrief: NurtureContentBrief;
  draft: NurtureStepDraft | null;
};

export type NurturePlanStatus = "draft" | "needs_review" | "approved";
export type NurtureGeneratedBy = "ai" | "human_edited" | "user_created";
export type NurtureTrackStatus = "draft" | "active" | "paused" | "completed" | "archived";

export type NurtureTrack = {
  id: string;
  name: string;
  audience: Record<string, unknown> | string;
  objective: string | null;
  messagingApproach: string | null;
  entryCriteria: string[];
  exitCriteria: string[];
  pauseCriteria: string[];
  stages: NurtureStage[];
  status: NurtureTrackStatus;
  planStatus: NurturePlanStatus;
  generatedBy: NurtureGeneratedBy;
  createdBy: string | null;
  updatedBy: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type NurturePerformanceTrackSummary = { trackId: string | null; trackName: string | null; leadCount: number; progressedCount: number; stalledCount: number; reEngagedCount: number };
export type NurturePerformanceResult = { tracks: NurturePerformanceTrackSummary[]; dataQualityNotes: string[] };

export type NurtureLeadListQuery = { classification?: NurtureClassification; engagementTrend?: NurtureEngagementTrend; trackId?: string };

const nurtureLeadQuery = (query: NurtureLeadListQuery) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value));
  return params.size ? `?${params}` : "";
};

export const loadNurtureOverview = (token: string, tenantId: string) => request<NurtureOverview>("/api/v1/lead-nurturing/overview", token, tenantId);

export const runNurtureAnalysis = (token: string, tenantId: string) => request<NurtureRunResult>("/api/v1/lead-nurturing/run", token, tenantId, { method: "POST" });

export const loadNurtureLeads = (token: string, tenantId: string, query: NurtureLeadListQuery = {}) =>
  request<{ data: NurtureLeadSnapshot[] }>(`/api/v1/lead-nurturing/leads${nurtureLeadQuery(query)}`, token, tenantId);

export const loadNurtureLeadDetail = (token: string, tenantId: string, leadId: string) =>
  request<NurtureLeadDetail>(`/api/v1/lead-nurturing/leads/${encodeURIComponent(leadId)}`, token, tenantId);

export const loadNurtureLeadHistory = (token: string, tenantId: string, leadId: string) =>
  request<{ data: NurtureLeadHistoryEntry[] }>(`/api/v1/lead-nurturing/leads/${encodeURIComponent(leadId)}/history`, token, tenantId);

export const explainNurtureLead = (token: string, tenantId: string, leadId: string) =>
  request<NurtureExplainResult>(`/api/v1/lead-nurturing/leads/${encodeURIComponent(leadId)}/explain`, token, tenantId, { method: "POST" });

export const assignNurtureLeadTrack = (token: string, tenantId: string, leadId: string, trackId: string | null) =>
  request<NurtureLeadDetail>(`/api/v1/lead-nurturing/leads/${encodeURIComponent(leadId)}/track`, token, tenantId, { method: "POST", body: JSON.stringify({ trackId }) });

export const loadNurtureSegments = (token: string, tenantId: string) => request<{ data: NurtureSegment[] }>("/api/v1/lead-nurturing/segments", token, tenantId);

export const proposeNurtureTrack = (token: string, tenantId: string, segmentKey: string) =>
  request<NurtureTrack>(`/api/v1/lead-nurturing/segments/${encodeURIComponent(segmentKey)}/propose-track`, token, tenantId, { method: "POST" });

export const loadNurtureTracks = (token: string, tenantId: string) => request<{ data: NurtureTrack[] }>("/api/v1/lead-nurturing/tracks", token, tenantId);

export const loadNurtureTrackDetail = (token: string, tenantId: string, trackId: string) =>
  request<NurtureTrack>(`/api/v1/lead-nurturing/tracks/${encodeURIComponent(trackId)}`, token, tenantId);

export const editNurtureTrack = (
  token: string,
  tenantId: string,
  trackId: string,
  edits: Partial<{ name: string; objective: string; messagingApproach: string; entryCriteria: string[]; exitCriteria: string[]; pauseCriteria: string[]; stages: NurtureStage[] }>,
) => request<NurtureTrack>(`/api/v1/lead-nurturing/tracks/${encodeURIComponent(trackId)}`, token, tenantId, { method: "PUT", body: JSON.stringify(edits) });

export const decideNurtureTrack = (token: string, tenantId: string, trackId: string, decision: "approved" | "rejected") =>
  request<NurtureTrack>(`/api/v1/lead-nurturing/tracks/${encodeURIComponent(trackId)}/decision`, token, tenantId, { method: "POST", body: JSON.stringify({ decision }) });

export const generateNurtureStepDraft = (token: string, tenantId: string, trackId: string, stepNumber: number) =>
  request<NurtureTrack>(`/api/v1/lead-nurturing/tracks/${encodeURIComponent(trackId)}/steps/${stepNumber}/draft`, token, tenantId, { method: "POST" });

export const decideNurtureStepDraft = (token: string, tenantId: string, trackId: string, stepNumber: number, decision: "approved" | "rejected") =>
  request<NurtureTrack>(`/api/v1/lead-nurturing/tracks/${encodeURIComponent(trackId)}/steps/${stepNumber}/draft/decision`, token, tenantId, { method: "POST", body: JSON.stringify({ decision }) });

export const loadNurtureAlerts = (token: string, tenantId: string) => request<{ data: NurtureAlert[] }>("/api/v1/lead-nurturing/alerts", token, tenantId);

export const decideNurtureAlert = (token: string, tenantId: string, id: string, decision: "approved" | "rejected", reason?: string) =>
  request<NurtureAlert>(`/api/v1/lead-nurturing/alerts/${encodeURIComponent(id)}/decision`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ decision, ...(reason ? { reason } : {}) }),
  });

export const loadNurturePerformance = (token: string, tenantId: string) => request<NurturePerformanceResult>("/api/v1/lead-nurturing/performance", token, tenantId);

export type ForecastPeriodKind = "current_month" | "next_month" | "current_quarter" | "next_quarter" | "current_year";
export type ForecastTargetStatus = "on_track" | "at_risk" | "below_target" | "above_target" | "insufficient_data";
export type ForecastProbabilitySource = "crm" | "segment:industry+band" | "segment:band" | "segment:industry" | "segment:overall" | "unavailable";
export type ForecastChangeKind = "new" | "removed" | "stage_changed" | "close_date_shifted" | "amount_changed";

export type ForecastPeriodRange = { start: string; end: string };

export type ForecastSegment = { segment: string; expectedRevenue: number; conversionRateUsed: number | null; probabilitySource: ForecastProbabilitySource | null; opportunityCount: number };

export type ForecastMarketingContribution = {
  available: boolean;
  reason: string | null;
  bySource: Array<{ source: string; expectedRevenue: number; opportunityCount: number }>;
  contributionNotes: string[];
};

export type ForecastRisk = { key: string; title: string; description: string; severity: "high" | "medium" | "low" };
export type ForecastDataQualityFlag = { key: string; title: string; description: string; impact: string };
export type ForecastChangeDriver = { kind: ForecastChangeKind; opportunityId: string; opportunityName: string; weightedValueDelta: number; description: string };

export type RevenueForecastResult = {
  hasRun: true;
  runId: string;
  periodKind: ForecastPeriodKind;
  period: ForecastPeriodRange;
  createdAt: string;
  forecastSummary: { expectedRevenue: number; conservativeRevenue: number; upsideRevenue: number; confidenceLevel: ConfidenceLevel; confidenceReasons: string[] };
  targetComparison: { targetRevenue: number | null; varianceAmount: number | null; variancePercentage: number | null; status: ForecastTargetStatus };
  forecastChange: { previousForecast: number | null; currentForecast: number; changeAmount: number | null; changePercentage: number | null; primaryDrivers: ForecastChangeDriver[] };
  segmentForecasts: ForecastSegment[];
  marketingContribution: ForecastMarketingContribution;
  forecastRisks: ForecastRisk[];
  dataQualityFlags: ForecastDataQualityFlag[];
  forecastAssumptions: string[];
  narrative: string | null;
  recommendations: string[];
  humanReviewRequired: boolean;
};
export type NoRevenueForecastYet = { hasRun: false };

export type RevenueForecastAccuracyEntry = {
  periodKind: ForecastPeriodKind;
  periodStart: string;
  periodEnd: string;
  expectedRevenue: number | null;
  actualRevenue: number;
  absoluteError: number | null;
  percentageError: number | null;
  bias: "over" | "under" | "accurate" | null;
};
export type RevenueForecastAccuracyResult = { available: boolean; reason: string | null; periods: RevenueForecastAccuracyEntry[] };

export type RevenueForecastAlert = {
  id: string;
  title: string;
  rationale: string;
  evidence: string[];
  confidence: number | null;
  status: "pending" | "approved" | "rejected" | "expired";
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
};

export const loadRevenueForecast = (token: string, tenantId: string, period: ForecastPeriodKind) =>
  request<RevenueForecastResult | NoRevenueForecastYet>(`/api/v1/revenue-forecasting/forecast?period=${encodeURIComponent(period)}`, token, tenantId);

export const runRevenueForecast = (token: string, tenantId: string, period: ForecastPeriodKind) =>
  request<RevenueForecastResult>("/api/v1/revenue-forecasting/run", token, tenantId, { method: "POST", body: JSON.stringify({ period }) });

export const loadRevenueForecastHistory = (token: string, tenantId: string, period: ForecastPeriodKind) =>
  request<{ data: RevenueForecastResult[] }>(`/api/v1/revenue-forecasting/forecast/history?period=${encodeURIComponent(period)}`, token, tenantId);

export const loadRevenueForecastAccuracy = (token: string, tenantId: string) => request<RevenueForecastAccuracyResult>("/api/v1/revenue-forecasting/accuracy", token, tenantId);

export const loadRevenueForecastAlerts = (token: string, tenantId: string) => request<{ data: RevenueForecastAlert[] }>("/api/v1/revenue-forecasting/alerts", token, tenantId);

export const decideRevenueForecastAlert = (token: string, tenantId: string, id: string, decision: "approved" | "rejected", reason?: string) =>
  request<RevenueForecastAlert>(`/api/v1/revenue-forecasting/alerts/${encodeURIComponent(id)}/decision`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ decision, ...(reason ? { reason } : {}) }),
  });

export type ConversionRateSnapshot = {
  available: boolean;
  reason: string | null;
  sufficientHistory: boolean;
  currentSessions: number | null;
  previousSessions: number | null;
  currentConversions: number | null;
  previousConversions: number | null;
  currentRatePct: number | null;
  previousRatePct: number | null;
  changePct: number | null;
  windowDays: number;
};

export type StructuralPageFinding = {
  key: "no_cta_no_form" | "low_score" | "thin_content" | "slow_response";
  url: string;
  title: string;
  issue: string;
  evidence: string[];
};

export type ConversionOpportunity = {
  key: string;
  title: string;
  metric: string;
  currentValue: string;
  comparison: string;
  change: string;
  certainty: Certainty;
  evidence: string[];
  reach: "high" | "medium" | "low";
};

export type PerformanceContextSummary = {
  available: boolean;
  reason: string | null;
  runAt: string | null;
  overallHealth: OverallHealth | null;
  winLoss: { currentWinRate: number | null; previousWinRate: number | null; changePct: number | null } | null;
  leadConversion: { currentRate: number | null; previousRate: number | null; changePct: number | null } | null;
};

export type LeadQualityContext = {
  available: boolean;
  reason: string | null;
  averageScore: number | null;
  highBandCount: number | null;
  mediumBandCount: number | null;
  lowBandCount: number | null;
  scoredCount: number | null;
};

export type BuyingIntentContext = {
  available: boolean;
  reason: string | null;
  highIntentCount: number | null;
  risingCount: number | null;
  fallingCount: number | null;
  newlyActiveCount: number | null;
  scoredAccountCount: number | null;
};

export type ExperimentRecommendationType = "run_experiment" | "gather_more_evidence" | "monitor" | "do_not_prioritise_now";
export type PriorityBucket = "run_next" | "validate_further" | "monitor" | "deprioritise";

export type ConversionExperiment = {
  id: string;
  title: string;
  problem: string;
  hypothesis: string;
  control: string;
  variant: string;
  targetAudience: string;
  primaryMetric: string;
  secondaryMetrics: string[];
  guardrailMetrics: string[];
  expectedImpact: "high" | "medium" | "low";
  confidence: ConfidenceLevel;
  effort: "high" | "medium" | "low";
  reach: "high" | "medium" | "low";
  baseline: string;
  dependencies: string[];
  measurementPlan: string;
  recommendation: ExperimentRecommendationType;
  priorityBucket: PriorityBucket;
  status: "pending" | "approved" | "rejected" | "expired";
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
};

export type ConversionAnalysisResult = {
  hasRun: true;
  runId: string;
  startedAt: string;
  conversionRate: ConversionRateSnapshot;
  structuralFindings: StructuralPageFinding[];
  performanceContext: PerformanceContextSummary;
  leadQualityContext: LeadQualityContext;
  buyingIntentContext: BuyingIntentContext;
  opportunities: ConversionOpportunity[];
  dataQualityNotes: DataQualityNote[];
  experiments: ConversionExperiment[];
};
export type NoConversionAnalysisYet = { hasRun: false };

export type ConversionAnalysisHistoryEntry = {
  runId: string;
  startedAt: string;
  opportunityCount: number;
  experimentCount: number;
  experiments: ConversionExperiment[];
};

export type HypothesisOutcome = "supported" | "not_supported" | "inconclusive";
export type RecommendedAction = "scale" | "iterate" | "retest" | "stop" | "investigate_further";

export type ConversionExperimentOutcome = {
  experimentId: string;
  title: string;
  recommendation: ExperimentRecommendationType;
  baselineRunAt: string;
  available: boolean;
  reason: string | null;
  conversionRateBefore: number | null;
  conversionRateAfter: number | null;
  hypothesisSupported: HypothesisOutcome | null;
  recommendedAction: RecommendedAction | null;
};

export const loadConversionAnalysis = (token: string, tenantId: string) =>
  request<ConversionAnalysisResult | NoConversionAnalysisYet>("/api/v1/conversion-optimisation/analysis", token, tenantId);

export const runConversionAnalysis = (token: string, tenantId: string) =>
  request<ConversionAnalysisResult>("/api/v1/conversion-optimisation/analysis/run", token, tenantId, { method: "POST" });

export const loadConversionAnalysisHistory = (token: string, tenantId: string) =>
  request<{ data: ConversionAnalysisHistoryEntry[] }>("/api/v1/conversion-optimisation/analysis/history", token, tenantId);

export const decideConversionExperiment = (token: string, tenantId: string, id: string, decision: "approved" | "rejected", reason?: string) =>
  request<ConversionExperiment>(`/api/v1/conversion-optimisation/experiments/${encodeURIComponent(id)}/decision`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ decision, ...(reason ? { reason } : {}) }),
  });

export const loadConversionExperimentOutcomes = (token: string, tenantId: string) =>
  request<{ data: ConversionExperimentOutcome[] }>("/api/v1/conversion-optimisation/experiments/outcomes", token, tenantId);

export type ReputationSourceCategory = "owned_website" | "customer_reviews" | "social_discussion" | "news_coverage" | "crm_notes";
export type ReputationSourceCoverageEntry = { source: ReputationSourceCategory; available: boolean; reason: string | null };

export type ReputationSignalType = "testimonial_mention" | "success_story_mention" | "case_study_mention" | "trust_badge_mention" | "rating_claim_mention";
export type ReputationSignal = {
  signalId: string;
  sourceType: ReputationSourceCategory;
  sourceName: string;
  sourceReference: string;
  discoveredAt: string;
  signalType: ReputationSignalType;
  topic: string;
  sentiment: "positive" | "negative" | "neutral";
  evidence: string[];
  confidence: ConfidenceLevel;
};

export type ReputationTheme = {
  key: string;
  title: string;
  topic: string;
  maturity: "isolated" | "emerging" | "established";
  distinctSourceCount: number;
  signalCount: number;
  signals: ReputationSignal[];
};

export type ReputationTrendState = "improving" | "stable" | "emerging_concern" | "deteriorating" | "recovering" | "insufficient_evidence";
export type ReputationAlertType = "emerging_negative_theme" | "reputation_deterioration" | "trust_or_credibility_concern" | "emerging_positive_momentum" | "conflicting_reputation_signals";
export type ReputationSeverity = "low" | "medium" | "high" | "critical";

export type ReputationAlert = {
  id: string;
  title: string;
  whatHappened: string;
  whyItMatters: string;
  recommendedNextSteps: string[];
  alertType: ReputationAlertType;
  severity: ReputationSeverity;
  confidence: ConfidenceLevel;
  supportingThemeKeys: string[];
  sourceCategoriesInvolved: ReputationSourceCategory[];
  evidence: string[];
  status: "pending" | "approved" | "rejected" | "expired";
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
};

export type ReputationAnalysisResult = {
  hasRun: true;
  runId: string;
  startedAt: string;
  sourceCoverage: ReputationSourceCoverageEntry[];
  signals: ReputationSignal[];
  themes: ReputationTheme[];
  trend: ReputationTrendState;
  dataQualityNotes: DataQualityNote[];
  alerts: ReputationAlert[];
  llmAvailable: boolean;
  llmInvoked: boolean;
};
export type NoReputationAnalysisYet = { hasRun: false };

export type ReputationAnalysisHistoryEntry = {
  runId: string;
  startedAt: string;
  trend: ReputationTrendState;
  alertCount: number;
  alerts: ReputationAlert[];
};

export const loadReputationAnalysis = (token: string, tenantId: string) =>
  request<ReputationAnalysisResult | NoReputationAnalysisYet>("/api/v1/reputation-intelligence/analysis", token, tenantId);

export const runReputationAnalysis = (token: string, tenantId: string) =>
  request<ReputationAnalysisResult>("/api/v1/reputation-intelligence/analysis/run", token, tenantId, { method: "POST" });

export const loadReputationAnalysisHistory = (token: string, tenantId: string) =>
  request<{ data: ReputationAnalysisHistoryEntry[] }>("/api/v1/reputation-intelligence/analysis/history", token, tenantId);

export const decideReputationAlert = (token: string, tenantId: string, id: string, decision: "approved" | "rejected", reason?: string) =>
  request<ReputationAlert>(`/api/v1/reputation-intelligence/alerts/${encodeURIComponent(id)}/decision`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ decision, ...(reason ? { reason } : {}) }),
  });

export type ContentDraftMixEntry = { contentType: string; status: string; count: number };
export type AssetRequirementCoverageEntry = { status: string; count: number };
export type ContentPillarCoverage = { unmapped: number; total: number };

export type ContentMarketingStrategyContext = {
  available: boolean;
  reason: string | null;
  approved: boolean;
  priorityAudiences: Array<{ audience: string; buyerRole: string; need: string }>;
  messageThemes: string[];
  messagingGaps: string[];
};

export type ContentBusinessContext = {
  available: boolean;
  reason: string | null;
  icpFacts: Array<{ field: string; label: string; value: string }>;
};

export type ContentCoverageFinding = {
  key: "stuck_asset_requirements" | "content_type_concentration" | "unmapped_messaging_pillars";
  title: string;
  metric: string;
  currentValue: string;
  comparison: string;
  certainty: Certainty;
  evidence: string[];
};

export type ContentInitiativeType = "create_now" | "define_further" | "monitor" | "deprioritise";

export type ContentInitiative = {
  id: string;
  title: string;
  problem: string;
  rationale: string;
  contentType: string;
  targetAudience: string;
  recommendedPhase: string;
  dependencies: string[];
  impact: "high" | "medium" | "low";
  effort: "high" | "medium" | "low";
  confidence: ConfidenceLevel;
  recommendation: ContentInitiativeType;
  priorityBucket: PriorityBucket;
  status: "pending" | "approved" | "rejected" | "expired";
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
};

export type ContentStrategyAnalysisResult = {
  hasRun: true;
  runId: string;
  startedAt: string;
  contentDraftMix: ContentDraftMixEntry[];
  assetRequirementCoverage: AssetRequirementCoverageEntry[];
  pillarCoverage: ContentPillarCoverage;
  marketingStrategyContext: ContentMarketingStrategyContext;
  businessContext: ContentBusinessContext;
  findings: ContentCoverageFinding[];
  dataQualityNotes: DataQualityNote[];
  initiatives: ContentInitiative[];
};
export type NoContentStrategyAnalysisYet = { hasRun: false };

export type ContentStrategyAnalysisHistoryEntry = {
  runId: string;
  startedAt: string;
  findingCount: number;
  initiativeCount: number;
  initiatives: ContentInitiative[];
};

export const loadContentStrategyAnalysis = (token: string, tenantId: string) =>
  request<ContentStrategyAnalysisResult | NoContentStrategyAnalysisYet>("/api/v1/content-strategy/analysis", token, tenantId);

export const runContentStrategyAnalysis = (token: string, tenantId: string) =>
  request<ContentStrategyAnalysisResult>("/api/v1/content-strategy/analysis/run", token, tenantId, { method: "POST" });

export const loadContentStrategyAnalysisHistory = (token: string, tenantId: string) =>
  request<{ data: ContentStrategyAnalysisHistoryEntry[] }>("/api/v1/content-strategy/analysis/history", token, tenantId);

export const decideContentInitiative = (token: string, tenantId: string, id: string, decision: "approved" | "rejected", reason?: string) =>
  request<ContentInitiative>(`/api/v1/content-strategy/initiatives/${encodeURIComponent(id)}/decision`, token, tenantId, {
    method: "POST",
    body: JSON.stringify({ decision, ...(reason ? { reason } : {}) }),
  });
